import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontWeights,
  minBreakpointQuery,
  sectionPaddings,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Container, HtmlContent, Link, Tag } from './ui';
import { hasStructuredText } from '../utils';
import BlogMeta from './BlogMeta';

const StyledBlogListingBanner = styled.section`
  ${sectionPaddings('45px', '90px')};
  background-color: ${brandColours.primary};
`;

const StyledHeading = styled.h1`
  ${visuallyHidden()};
`;

const StyledInner = styled.article`
  display: grid;
  gap: 30px;

  ${minBreakpointQuery.tiny`
    gap: 40px;
  `}

  ${minBreakpointQuery.small`
    gap: 50px;
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
  `}

  ${minBreakpointQuery.medium`
    gap: 60px;
  `}

  ${minBreakpointQuery.large`
    gap: 70px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 80px;
  `}
`;

const StyledContent = styled.div``;

const StyledHeader = styled.header`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 20px;
`;

const StyledSubheading = styled(Tag)`
  display: inline-block;
  background-color: ${brandColours.nonary};
  border-color: ${brandColours.nonary};
`;

const StyledPostHeading = styled.h3`
  color: ${brandColours.secondary};
  font-weight: ${fontWeights.semibold};
  ${fluidFontSize(
    '24px',
    '40px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 1.2;

  a {
    transition: ${standardTransition('color')};

    &:hover {
      color: ${brandColours.tertiary};
    }
  }
`;

const StyledSummary = styled(HtmlContent)`
  margin-top: 30px;
  p {
    line-height: 1.7;
  }
`;

const StyledText = styled.p`
  margin-top: 30px;
  line-height: 1.7;
`;

const StyledImageWrapper = styled(Link)`
  border-radius: 20px;
  overflow: hidden;
`;

const BlogListingBanner = ({ heading, item: intialItem }) => {
  const {
    allDatoCmsBlog: { nodes },
  } = useStaticQuery(graphql`
    query BlogListingBannerQuery {
      allDatoCmsBlog(sort: { meta: { firstPublishedAt: DESC } }) {
        nodes {
          ...BlogCardFragment
        }
      }
    }
  `);

  const subheading = intialItem ? 'Featured Article' : 'Latest Article';
  const item = intialItem || nodes[0];

  return (
    <StyledBlogListingBanner>
      <Container>
        <StyledHeading>{heading}</StyledHeading>
        <StyledInner>
          <StyledContent>
            <StyledHeader>
              <StyledSubheading forwardedAs="h2">{subheading}</StyledSubheading>
              {item.categories.map((category, id) => (
                <Link key={id} to={category}>
                  <Tag>{category.title}</Tag>
                </Link>
              ))}
            </StyledHeader>
            <StyledPostHeading>
              <Link to={item}>{item.heading}</Link>
            </StyledPostHeading>
            {hasStructuredText(item.summaryNew) ? (
              <StyledSummary content={item.summaryNew} />
            ) : (
              <StyledText>{item.summary}</StyledText>
            )}
            <BlogMeta author={item.author} readTime={item.readTime} />
          </StyledContent>
          <StyledImageWrapper to={item}>
            <GatsbyImage
              image={item.featuredImage.gatsbyImageData}
              alt={item.featuredImage.alt}
            />
          </StyledImageWrapper>
        </StyledInner>
      </Container>
    </StyledBlogListingBanner>
  );
};

export default BlogListingBanner;
